import { z } from 'zod';
import {
  CONFIRM_PHRASE_GO_LIVE,
  CONFIRM_PHRASE_PANIC,
  ENGINE_STATES,
  EXECUTION_MODES,
  STRATEGY_KEYS,
} from '../enums';
import { decimalStringSchema, uuidSchema } from './common';

/** Engine control contract (API_DOCS.md — Engine Control, ARCHITECTURE.md §5). */

export const circuitBreakerStatusSchema = z.object({
  tripped: z.boolean(),
  drawdownPct: decimalStringSchema,
  limitPct: decimalStringSchema,
  trippedAt: z.string().datetime().nullable(),
});
export type CircuitBreakerStatus = z.infer<typeof circuitBreakerStatusSchema>;

export const engineStatusResponseSchema = z.object({
  state: z.enum(ENGINE_STATES),
  mode: z.enum(EXECUTION_MODES),
  uptimeSec: z.number().int().nonnegative(),
  /** Every running entry strategy; empty when none is switched on. */
  activeStrategies: z.array(z.enum(STRATEGY_KEYS)),
  openPositions: z.number().int().nonnegative(),
  circuitBreaker: circuitBreakerStatusSchema,
});
export type EngineStatusResponse = z.infer<typeof engineStatusResponseSchema>;

export const engineStateResponseSchema = z.object({
  state: z.enum(ENGINE_STATES),
});
export type EngineStateResponse = z.infer<typeof engineStateResponseSchema>;

/**
 * Switching to live requires the exact typed phrase plus verified Binance credentials
 * (ARCHITECTURE.md §10, AI_RULES §13). The phrase check lives in the schema so no route
 * can forget it.
 */
export const modeChangeRequestSchema = z
  .object({
    mode: z.enum(EXECUTION_MODES),
    confirmPhrase: z.string().optional(),
    force: z.boolean().default(false),
  })
  .refine(
    (v) => v.mode !== 'live' || v.confirmPhrase === CONFIRM_PHRASE_GO_LIVE,
    { message: `live mode requires confirmPhrase "${CONFIRM_PHRASE_GO_LIVE}"`, path: ['confirmPhrase'] },
  );
export type ModeChangeRequest = z.infer<typeof modeChangeRequestSchema>;

export const panicRequestSchema = z.object({
  confirmPhrase: z.literal(CONFIRM_PHRASE_PANIC),
});
export type PanicRequest = z.infer<typeof panicRequestSchema>;

export const panicResultSchema = z.object({
  state: z.enum(ENGINE_STATES),
  results: z.array(
    z.object({
      tradeId: uuidSchema,
      symbol: z.string(),
      status: z.enum(['closed', 'failed']),
      exitPrice: decimalStringSchema.nullable(),
      error: z.string().optional(),
    }),
  ),
});
export type PanicResult = z.infer<typeof panicResultSchema>;

export const circuitBreakerResetResponseSchema = z.object({ tripped: z.literal(false) });
export type CircuitBreakerResetResponse = z.infer<typeof circuitBreakerResetResponseSchema>;
